import React, { useEffect, useRef, useState } from 'react'

import { trackEvent } from 'lib/util/analytics'

import { CONFIGURE_EXAMPLES, ConfigureExample } from './examples'

const CHAR_MS = 25

type Props = {
  onFilled: (example: ConfigureExample, options: { glow: boolean }) => void
}

function reducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  )
}

export default function ExamplePrompts({ onFilled }: Props) {
  const [activeId, setActiveId] = useState<string | null>(null)
  const [run, setRun] = useState(0)
  const [shown, setShown] = useState('')
  const onFilledRef = useRef(onFilled)
  onFilledRef.current = onFilled

  useEffect(() => {
    if (!activeId) return
    const example = CONFIGURE_EXAMPLES.find(item => item.id === activeId)
    if (!example) return

    if (reducedMotion()) {
      setShown(example.prompt)
      onFilledRef.current(example, { glow: false })
      return
    }

    setShown('')
    let index = 0
    const timer = window.setInterval(() => {
      index += 1
      setShown(example.prompt.slice(0, index))
      if (index >= example.prompt.length) {
        window.clearInterval(timer)
        onFilledRef.current(example, { glow: true })
      }
    }, CHAR_MS)

    return () => {
      window.clearInterval(timer)
    }
  }, [activeId, run])

  return (
    <div style={{ marginBottom: '1.25rem' }}>
      <div
        style={{ fontSize: '0.85rem', color: '#666', marginBottom: '0.4rem' }}
      >
        Try an example:
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem' }}>
        {CONFIGURE_EXAMPLES.map(example => (
          <button
            key={example.id}
            type="button"
            data-testid={`example-${example.id}`}
            aria-pressed={activeId === example.id}
            onClick={() => {
              trackEvent('configure_example_click', {
                example: example.id,
                topic: example.topic,
                input_type: example.mode,
              })
              setActiveId(example.id)
              setRun(n => n + 1)
            }}
            style={{
              cursor: 'pointer',
              fontWeight: 700,
              fontSize: '0.78571429rem',
              lineHeight: 1,
              padding: '0.5833em 0.833em',
              margin: 0,
              background: '#fff',
              color: 'rgba(0,0,0,0.87)',
              border: '1px solid rgba(34,36,38,0.15)',
              borderRadius: '0.28571429rem',
            }}
          >
            {example.prompt}
          </button>
        ))}
      </div>
      <div
        data-testid="example-sentence"
        aria-live="polite"
        style={{ minHeight: '1.25rem', marginTop: '0.6rem' }}
      >
        {shown}
      </div>
      <div
        data-testid="example-note"
        style={{
          color: '#767676',
          fontSize: '0.85rem',
          marginTop: '0.15rem',
        }}
      >
        Subscribers can describe it in their own words after signing up.
      </div>
    </div>
  )
}
